import { Link } from 'react-router-dom';
import { dateHeureDeTunis } from '@rh/shared';
import { Vide } from '../composants/Etat';
import { nomComplet } from '../format';
import { useFile } from '../horsligne/contexte-file';
import { maintenantServeur } from '../horsligne/horloge';
import { memoire } from '../horsligne/memoire';
import { MESSAGE_ABANDON, heureDeTunis, jourDeTunis, libelleContact } from '../horsligne/textes';
import type { ResumeEnvoi } from '../horsligne/types';

/**
 * Saisies de ce téléphone (docs/UI.md § Écran 3, mode hors ligne) : ce qui
 * attend l'envoi, ce qui a été refusé, les brouillons. Le nom du candidat ne
 * vient que de la mémoire de l'onglet ; sinon, le type et l'heure du contact.
 */

function titre(resume: ResumeEnvoi): string {
  const fiche = memoire.fiche(resume.candidatureId);
  if (!fiche) return libelleContact(resume.type, resume.debuteLe);
  const nom = nomComplet({ prenom: fiche.prenom, nom: fiche.nom, anonymisee: fiche.anonymiseeLe !== null });
  return `${resume.type === 'telephonique' ? 'Appel' : 'Entretien'} · ${nom}`;
}

function etapesRestantes(resume: ResumeEnvoi): string | null {
  const etapes = [
    resume.etapes.enregistrement ? 'modifications' : null,
    resume.etapes.fin === 'cloture' ? 'clôture' : resume.etapes.fin === 'evaluation' ? 'évaluation' : null,
  ].filter(Boolean);
  return etapes.length > 0 ? `Reste à envoyer : ${etapes.join(', ')}` : null;
}

function CarteEnvoi({ resume, maintenant }: { resume: ResumeEnvoi; maintenant: number }) {
  const { abandonner } = useFile();
  const efface = resume.refus?.contenuEfface === true;
  const lien = `/contacts/${resume.contactId}`;

  function effacer(): void {
    if (window.confirm(MESSAGE_ABANDON)) void abandonner(resume.contactId);
  }

  const etat =
    resume.etat === 'brouillon'
      ? 'Brouillon non enregistré'
      : resume.etat === 'refuse'
        ? 'Refusée par le serveur'
        : 'En attente d’envoi';

  return (
    <li className={`carte-envoi carte-envoi--${resume.etat}`}>
      <div className="ligne1">
        <strong>{titre(resume)}</strong>
        <span className={resume.etat === 'refuse' ? 'signal-alerte' : 'signal-info'}>{etat}</span>
      </div>
      <p className="meta">
        <span>{libelleContact(resume.type, resume.debuteLe)}</span>
        {etapesRestantes(resume) && <span>{etapesRestantes(resume)}</span>}
      </p>
      {resume.refus && <p className="refus-envoi__message">Motif : {resume.refus.message}</p>}
      {resume.etat === 'en_attente' && resume.tentatives > 0 && (
        <p className="meta">
          <span>
            {resume.tentatives} essai{resume.tentatives > 1 ? 's' : ''}
            {resume.dernierePanne ? ` · dernier échec : ${resume.dernierePanne}` : ''}
          </span>
          {resume.prochaineTentativeLe > maintenant && (
            <span>prochain essai à {heureDeTunis(resume.prochaineTentativeLe)}</span>
          )}
        </p>
      )}
      {!efface && (
        <p className="mention">
          Effacée le {jourDeTunis(resume.expireLe)} si elle n’est pas envoyée
          {resume.etat === 'brouillon' ? ` (modifiée le ${dateHeureDeTunis(resume.modifieLe)})` : ''}
        </p>
      )}
      <div className="actions">
        {efface ? (
          <button type="button" className="bouton" onClick={() => void abandonner(resume.contactId)}>
            J’ai compris
          </button>
        ) : (
          <>
            {resume.etat !== 'en_attente' && (
              <Link className="bouton-second lien-bouton" to={lien}>
                Revoir et renvoyer
              </Link>
            )}
            <button type="button" className="bouton-second bouton-danger" onClick={effacer}>
              Effacer
            </button>
          </>
        )}
      </div>
    </li>
  );
}

export function EnvoisEnAttente() {
  const { instantane, envoyerMaintenant } = useFile();
  const { resumes, enLigne, enAttente } = instantane;
  const maintenant = maintenantServeur();

  return (
    <section className="envois">
      <h2>Saisies de ce téléphone</h2>
      <p className="mention">
        Elles partent toutes seules dès que le réseau revient, dans l’ordre où vous les avez faites. Chacune est
        effacée de ce téléphone une fois reçue, et au plus tard 7 jours après la saisie.
      </p>
      {enAttente > 0 && enLigne && (
        <div className="actions">
          <button type="button" className="bouton" onClick={envoyerMaintenant}>
            Réessayer maintenant
          </button>
        </div>
      )}
      {resumes.length === 0 ? (
        <Vide enfants="Aucune saisie en attente : tout a été envoyé." />
      ) : (
        <ul className="liste-envois">
          {resumes.map((resume) => (
            <CarteEnvoi key={resume.contactId} resume={resume} maintenant={maintenant} />
          ))}
        </ul>
      )}
    </section>
  );
}
